import DriverAddRequest, {
  DriverAddRequestAddress,
  DriverAddRequestAddressType,
  DriverAddRequestCarOwnership,
  DriverAddRequestCitizenship,
  DriverAddRequestDriver,
  DriverAddRequestFormOfEmployment,
  DriverAddRequestLanguage,
  DriverAddRequestTaxiDriverAssociation,
} from "../../../api/raily/driver/add/driver-add.request";
import AddressType from "../../../types/address-type";
import UserCitizenship from "../../../types/user-citizenship";
import UserFormOfEmployment from "../../../types/user-form-of-employment";
import UserLanguage from "../../../types/user-language";
import UserVehicleOwnership from "../../../types/user-vehicle-ownership-form";
import DriverAddParams, {
  DriverAddParamsAddress,
  DriverAddParamsDriver,
  DriverAddParamsTaxiDriverAssociation,
} from "./driver-add-params";

const createFormOfEmployment = (
  formOfEmployment: UserFormOfEmployment,
): DriverAddRequestFormOfEmployment => {
  switch (formOfEmployment) {
    case UserFormOfEmployment.B2B:
      return DriverAddRequestFormOfEmployment.B2B;
    case UserFormOfEmployment.EMPLOYMENT:
      return DriverAddRequestFormOfEmployment.EMPLOYMENT;
  }
};

const createLanguage = (type: UserLanguage): DriverAddRequestLanguage => {
  switch (type) {
    case UserLanguage.BY:
      return DriverAddRequestLanguage.BY;
    case UserLanguage.CZ:
      return DriverAddRequestLanguage.CZ;
    case UserLanguage.DE:
      return DriverAddRequestLanguage.DE;
    case UserLanguage.LT:
      return DriverAddRequestLanguage.LT;
    case UserLanguage.PL:
      return DriverAddRequestLanguage.PL;
    case UserLanguage.RU:
      return DriverAddRequestLanguage.RU;
    case UserLanguage.SK:
      return DriverAddRequestLanguage.SK;
    case UserLanguage.UA:
      return DriverAddRequestLanguage.UA;
    case UserLanguage.EN:
      return DriverAddRequestLanguage.EN;
    case UserLanguage.ES:
      return DriverAddRequestLanguage.ES;
    case UserLanguage.FR:
      return DriverAddRequestLanguage.FR;
  }
};

const createLanguages = (
  languages: UserLanguage[],
): DriverAddRequestLanguage[] => {
  const requestLanguages: DriverAddRequestLanguage[] =
    languages.map(createLanguage);

  return requestLanguages;
};

const createCitizenship = (
  type: UserCitizenship,
): DriverAddRequestCitizenship => {
  switch (type) {
    case UserCitizenship.BY:
      return DriverAddRequestCitizenship.BY;
    case UserCitizenship.CZ:
      return DriverAddRequestCitizenship.CZ;
    case UserCitizenship.DE:
      return DriverAddRequestCitizenship.DE;
    case UserCitizenship.LT:
      return DriverAddRequestCitizenship.LT;
    case UserCitizenship.PL:
      return DriverAddRequestCitizenship.PL;
    case UserCitizenship.RU:
      return DriverAddRequestCitizenship.RU;
    case UserCitizenship.SK:
      return DriverAddRequestCitizenship.SK;
    case UserCitizenship.UA:
      return DriverAddRequestCitizenship.UA;
  }
};

const createCarOwnership = (
  type: UserVehicleOwnership,
): DriverAddRequestCarOwnership => {
  switch (type) {
    case UserVehicleOwnership.BUSINESS:
      return DriverAddRequestCarOwnership.BUSINESS;
    case UserVehicleOwnership.OWNER:
      return DriverAddRequestCarOwnership.OWNER;
  }
};

const createAddressType = (type: AddressType): DriverAddRequestAddressType => {
  switch (type) {
    case AddressType.MAILING:
      return DriverAddRequestAddressType.MAILING;
    case AddressType.STARTING:
      return DriverAddRequestAddressType.STARTING;
  }
};

const createTaxiDriverAssociation = (
  association: DriverAddParamsTaxiDriverAssociation,
): DriverAddRequestTaxiDriverAssociation => {
  if ("taxId" in association) {
    return {
      contractType: createFormOfEmployment(association.contractType),
      taxId: association.taxId,
      regon: association.regon,
      krsNo: association.krsNo,
      fleetPartnerId: association.fleetPartnerId,
      companyName: association.companyName,
      displayName: association.displayName,
      country: association.country,
      town: association.town,
      zipCode: association.zipCode,
      street: association.street,
      houseNo: association.houseNo,
      apartment: association.apartment,
      description: association.description,
      taxiCorporationId: association.taxiCorporationId,
    };
  } else {
    return {
      contractType: createFormOfEmployment(association.contractType),
      displayName: association.displayName,
      taxiCorporationId: association.taxiCorporationId,
      fleetPartnerId: association.fleetPartnerId,
    };
  }
};

const createTaxiDriverAssociations = (
  associations: DriverAddParamsTaxiDriverAssociation[],
): DriverAddRequestTaxiDriverAssociation[] => {
  return associations.map(createTaxiDriverAssociation);
};

const createAddress = (
  address: DriverAddParamsAddress,
): DriverAddRequestAddress => {
  return {
    apartment: address.apartment,
    country: address.country,
    description: address.description,
    houseNo: address.houseNo,
    lat: address.lat,
    lon: address.lon,
    street: address.street,
    town: address.town,
    type: createAddressType(address.type),
    zipCode: address.zipCode,
  };
};

const createAddresses = (
  addresses: DriverAddParamsAddress[],
): DriverAddRequestAddress[] => {
  const requestAddresses: DriverAddRequestAddress[] =
    addresses.map(createAddress);

  return requestAddresses;
};

const createDriver = (
  driver: DriverAddParamsDriver,
): DriverAddRequestDriver => {
  return {
    addresses: createAddresses(driver.addresses),
    birthDate: driver.birthDate,
    birthPlace: driver.birthPlace,
    carMake: driver.carMake,
    carModel: driver.carModel,
    carOwnership: createCarOwnership(driver.carOwnership),
    carProdYear: driver.carProdYear,
    carRegNo: driver.carRegNo,
    carSeatsNo: driver.carSeatsNo,
    citizenship: createCitizenship(driver.citizenship),
    experience: driver.experience,
    idNumber: driver.idNumber,
    languages: createLanguages(driver.languages),
    taxiDriverAssociations: createTaxiDriverAssociations(
      driver.taxiDriverAssociations,
    ),
  };
};

const create = (params: DriverAddParams): DriverAddRequest => {
  return {
    driver: createDriver(params.driver),
    email: params.email,
    firstName: params.firstName,
    lastName: params.lastName,
    mobilePrimary: params.mobilePrimary,
    mobilePrimaryPrefix: params.mobilePrimaryPrefix,
    mobileSecondary: params.mobileSecondary,
    mobileSecondaryPrefix: params.mobileSecondaryPrefix,
    password: params.password,
    username: params.username,
  };
};

const driverAddParamsFactory = {
  create,
};

export default driverAddParamsFactory;
